/**
 * Channel Sender Engine — the payer-side streaming state machine.
 *
 * States: idle → negotiating → streaming → closing → settling → settled | error
 *
 * Replaces source's payChannelSenderEngine with @qr20/channel bilateral signed states.
 * NO rolling-nonce mode. Every update is a bilateral signed state.
 */

import { canonicalizeQr20Json, hashQr20Payload } from '@qr20/envelope';
import { computeIntentDigest, canonicaliseStateUpdateForSigning } from '@qr20/channel';
import { type Qr20WebrtcSessionContract } from '@tokenstreaming/transport-webrtc';
import { floorMul } from '@tokenstreaming/shared';
import { SmoothDisplayEngine } from './smoothDisplay.js';
import type { StreamParams, ChannelState, EngineCallbacks, EngineState, StreamingEngine } from './types.js';

const COSIGN_TIMEOUT_MS = 10_000;
const MAX_COSIGN_RETRIES = 3;

export class ChannelSenderEngine implements StreamingEngine {
  private state: EngineState = 'idle';
  private channelState: ChannelState | null = null;
  private params: StreamParams;
  private callbacks: EngineCallbacks;
  private session: Qr20WebrtcSessionContract;
  private signMessage: (message: string) => Promise<string>;
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private startTime = 0;
  private smoothDisplay = new SmoothDisplayEngine();
  private intentDigest: string = '0x' + '00'.repeat(32);
  private pendingCosign = false;
  private cosignRetries = 0;
  private receiveIterator: AsyncIterator<unknown> | null = null;
  private stopped = false;

  constructor(
    params: StreamParams,
    session: Qr20WebrtcSessionContract,
    signMessage: (message: string) => Promise<string>,
    callbacks: EngineCallbacks
  ) {
    this.params = params;
    this.session = session;
    this.signMessage = signMessage;
    this.callbacks = callbacks;
  }

  async start(): Promise<void> {
    if (this.state !== 'idle') throw new Error(`Cannot start from state: ${this.state}`);

    this.setState('negotiating');
    this.startTime = Date.now();

    console.log('[ChSender] start()', {
      channelId: this.params.channelId,
      creator: this.params.creatorAddress,
      resolver: this.params.resolverAddress,
      deposit: this.params.deposit.toString(),
      ratePerSecAtomic: this.params.ratePerSecAtomic.toString(),
    });

    try {
      // Send channel-open intent
      await this.sendChannelOpen();

      // Wait for accept (cosigned initial state from receiver)
      await this.waitForAccept();

      console.log('[ChSender] channel-accept received → transitioning to streaming');

      // Start streaming tick loop
      this.setState('streaming');
      this.smoothDisplay.reset(0n, this.params.ratePerSecAtomic);
      this.startTickLoop();
      this.startReceiveLoop();
    } catch (err) {
      console.error('[ChSender] start() failed:', err);
      this.handleError(err instanceof Error ? err : new Error(String(err)));
    }
  }

  async stop(): Promise<void> {
    this.stopped = true;
    this.stopTickLoop();
    this.smoothDisplay.pause();
  }

  async close(): Promise<void> {
    if (this.state !== 'streaming' && this.state !== 'idle') {
      if (this.state === 'settled' || this.state === 'error') return;
    }

    this.setState('closing');
    this.stopTickLoop();

    try {
      // Send final close intent
      await this.sendCloseIntent();
      this.setState('settled');
    } catch (err) {
      this.handleError(err instanceof Error ? err : new Error(String(err)));
    }
  }

  getState(): EngineState {
    return this.state;
  }

  getChannelState(): ChannelState | null {
    return this.channelState;
  }

  getRole(): 'sender' {
    return 'sender';
  }

  getElapsedMs(): number {
    return Date.now() - this.startTime;
  }

  getSmoothAmount(): bigint {
    return this.smoothDisplay.getCurrentAmount();
  }

  // ─── Private: Negotiation ─────────────────────────────────────────────────

  private async sendChannelOpen(): Promise<void> {
    const openPayload = {
      type: 'channel-open',
      channelId: this.params.channelId,
      chain: this.params.chain,
      asset: this.params.asset,
      deposit: this.params.deposit.toString(),
      disputeWindowS: this.params.disputeWindowS,
      ratePerSecAtomic: this.params.ratePerSecAtomic.toString(),
      tickMs: this.params.tickMs,
      creatorAddress: this.params.creatorAddress,
      resolverAddress: this.params.resolverAddress,
      sigSchemes: ['secp256k1-eip191'],
    };

    const envelope = this.buildEnvelope(openPayload);
    envelope.id = await this.computeContentId(envelope);
    console.log('[ChSender] sending channel-open envelope', { id: envelope.id });
    await this.session.send(envelope as any);
    console.log('[ChSender] channel-open sent successfully');
  }

  private async waitForAccept(): Promise<void> {
    // Read from session until we get an accept message
    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Channel open timeout (30s)')), 30_000);
    });

    const waitForMessage = async (): Promise<void> => {
      console.log('[ChSender] waitForAccept: listener attached, awaiting channel-accept');
      for await (const envelope of this.session.receive()) {
        const msg = envelope as any;
        console.log('[ChSender] waitForAccept: received envelope', { id: msg?.id });
        // The accept envelope wraps the channel-accept type inside
        // intent.task.input_data (JSON-stringified), with intent.action
        // always set to 'agent_request'. Parse input_data to detect it.
        const intentData = msg?.intent?.task?.input_data;
        if (intentData) {
          try {
            const parsed = JSON.parse(intentData);
            console.log('[ChSender] waitForAccept: parsed intent type =', parsed.type);
            if (parsed.type === 'channel-accept') {
              // Initialize channel state at sequence 0
              this.channelState = {
                channelId: this.params.channelId,
                sequence: 0,
                creatorAmount: this.params.deposit,
                resolverAmount: 0n,
                intentDigest: this.intentDigest,
                lastUpdateTime: Date.now(),
              };
              this.callbacks.onBalanceUpdate(this.channelState);
              return;
            }
          } catch {
            // Not valid JSON or not an accept — continue
          }
        }
      }
    };

    await Promise.race([waitForMessage(), timeout]);
  }

  // ─── Private: Streaming ───────────────────────────────────────────────────

  private startTickLoop(): void {
    this.tickTimer = setInterval(() => {
      if (this.stopped || this.pendingCosign) return;
      void this.tick();
    }, this.params.tickMs);
  }

  private stopTickLoop(): void {
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  private async tick(): Promise<void> {
    if (!this.channelState || this.state !== 'streaming') return;

    const elapsedMs = Date.now() - this.startTime;
    const cumulativeStreamed = floorMul(this.params.ratePerSecAtomic, elapsedMs);

    // Cap at deposit
    const actualStreamed = cumulativeStreamed > this.params.deposit
      ? this.params.deposit
      : cumulativeStreamed;

    const creatorAmount = this.params.deposit - actualStreamed;
    const resolverAmount = actualStreamed;

    // Skip if no change
    if (
      creatorAmount === this.channelState.creatorAmount &&
      resolverAmount === this.channelState.resolverAmount
    ) {
      return;
    }

    const newSequence = this.channelState.sequence + 1;

    // Compute rolling intent digest
    const statePayload = {
      channelId: this.params.channelId,
      sequence: newSequence,
      creatorAmount: creatorAmount.toString(),
      resolverAmount: resolverAmount.toString(),
    };
    this.intentDigest = await computeIntentDigest(
      [JSON.parse(JSON.stringify(statePayload))],
      this.intentDigest
    );

    // Build and sign the state update
    const stateUpdate = {
      channel_id: this.params.channelId,
      sequence: newSequence,
      balances: {
        creator_amount: creatorAmount.toString(),
        resolver_amount: resolverAmount.toString(),
      },
      intent_digest: this.intentDigest,
    };

    const canonBytes = canonicaliseStateUpdateForSigning({
      ...stateUpdate,
      creator_sig: { scheme: 'secp256k1-eip191', value: '0x' },
      resolver_sig: { scheme: 'secp256k1-eip191', value: '0x' },
    });

    const signature = await this.signMessage(canonBytes);

    // Send state update envelope
    const updateEnvelope = {
      type: 'channel-update',
      ...stateUpdate,
      creator_sig: {
        scheme: 'secp256k1-eip191',
        value: signature,
      },
    };

    const updateMsg = this.buildEnvelope(updateEnvelope);
    updateMsg.id = await this.computeContentId(updateMsg);
    await this.session.send(updateMsg as any);

    this.pendingCosign = true;

    // Update local state optimistically
    this.channelState = {
      channelId: this.params.channelId,
      sequence: newSequence,
      creatorAmount,
      resolverAmount,
      intentDigest: this.intentDigest,
      lastUpdateTime: Date.now(),
    };
    this.callbacks.onBalanceUpdate(this.channelState);
    this.smoothDisplay.sync(actualStreamed);

    // Check if fully streamed
    if (creatorAmount === 0n) {
      await this.close();
    }
  }

  private startReceiveLoop(): void {
    void (async () => {
      try {
        for await (const envelope of this.session.receive()) {
          if (this.stopped) break;
          this.handleReceivedMessage(envelope as any);
        }
      } catch {
        // Session closed
      }
    })();
  }

  private handleReceivedMessage(msg: any): void {
    console.log('[ChSender] handleReceivedMessage envelope id=', msg?.id);
    // All engine messages wrap their type inside intent.task.input_data
    const intentData = msg?.intent?.task?.input_data;
    if (!intentData) return;
    let parsed: any;
    try {
      parsed = JSON.parse(intentData);
    } catch {
      return;
    }
    console.log('[ChSender] handleReceivedMessage type=', parsed.type);
    if (parsed.type === 'channel-update-ack') {
      // Cosigned state received - clear pending flag
      this.pendingCosign = false;
      this.cosignRetries = 0;
    } else if (parsed.type === 'channel-close') {
      // Receiver initiated close
      void this.close();
    }
  }

  // ─── Private: Close ───────────────────────────────────────────────────────

  private async sendCloseIntent(): Promise<void> {
    if (!this.channelState) return;

    const closePayload = {
      type: 'channel-close',
      channelId: this.params.channelId,
      sequence: this.channelState.sequence,
      creatorAmount: this.channelState.creatorAmount.toString(),
      resolverAmount: this.channelState.resolverAmount.toString(),
      intentDigest: this.channelState.intentDigest,
    };

    const closeMsg = this.buildEnvelope(closePayload);
    closeMsg.id = await this.computeContentId(closeMsg);
    await this.session.send(closeMsg as any);
  }

  // ─── Private: Helpers ─────────────────────────────────────────────────────

  private async computeContentId(envelope: Record<string, unknown>): Promise<string> {
    const stripped: Record<string, unknown> = { ...envelope };
    delete stripped.id;
    delete stripped.proof;
    if (typeof stripped.creator === 'object' && stripped.creator !== null) {
      stripped.creator = { ...(stripped.creator as Record<string, unknown>), signature: '' };
    }
    const hash = await hashQr20Payload(stripped, {
      crypto: { subtle: globalThis.crypto.subtle },
      algorithm: 'SHA-256',
    });
    return `sha256:${hash}`;
  }

  private buildEnvelope(intent: Record<string, unknown>): Record<string, unknown> {
    return {
      qr: '20',
      version: '1.0.0',
      type: 11,
      id: '', // computed after build via computeContentId
      timestamp: new Date().toISOString(),
      creator: {
        address: this.params.creatorAddress,
        signature: '',
        chain: this.params.chain,
      },
      routing: {
        mode: 'direct',
        target_resolvers: [this.params.resolverAddress],
        required_capabilities: ['channel.stream'],
        deadline: new Date(Date.now() + 3600_000).toISOString(),
        max_fee: '0',
      },
      encryption: { method: 'none', params: {} },
      intent: {
        action: 'agent_request',
        sender_agent: { id: 'sender', owner: this.params.creatorAddress, delegation_proof: 'self' },
        recipient_agent: { id: 'receiver', owner: this.params.resolverAddress },
        task: {
          description: JSON.stringify(intent),
          constraints: {
            max_cost: { amount: '0', token: this.params.asset },
            deadline: new Date(Date.now() + 3600_000).toISOString(),
            required_proofs: ['on_chain_hash'],
          },
          input_data: JSON.stringify(intent),
          output_schema: { ack: 'boolean' },
        },
        verification: { method: 'on_chain_hash', arbitration: this.params.creatorAddress },
      },
      channel: {
        channel_id: this.params.channelId,
        sequence: this.channelState?.sequence ?? 0,
      },
      extensions: {},
    };
  }

  private setState(state: EngineState): void {
    this.state = state;
    this.callbacks.onStateChange(state);
  }

  private handleError(error: Error): void {
    this.setState('error');
    this.stopTickLoop();
    this.callbacks.onError(error);
  }
}
